import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Trip } from '../../types';
import { tripsApi } from '../../api/trips.api';
import { toast } from '../ui/Toast';

// Matches `updateTripSchema` on the server; a longer name is rejected there.
const MAX_NAME = 100;

interface Props {
  trip: Trip;
  canEdit: boolean;
  /** Given the renamed trip, and the old one again if the save fails. */
  onRenamed: (trip: Trip) => void;
  /** How the name looks where it sits — the sidebar card and the mobile header
   *  style it completely differently, the editing behaviour is the same. */
  className: string;
  /** Clamping for the read-only text. Kept off the input, where `line-clamp-1`
   *  and `truncate` would fight with how a text field lays itself out. */
  displayClassName?: string;
}

/**
 * The trip name, editable in place. Same interaction as a packing item
 * (`PackingItemRow`): the text is a button that becomes an input, Enter or a
 * click outside save, Escape discards.
 */
export default function EditableTripName({ trip, canEdit, onRenamed, className, displayClassName = '' }: Props) {
  const { t } = useTranslation();
  // The draft doubles as the "is editing" flag: null = not editing, so there is
  // no second boolean that can disagree with it.
  const [draft, setDraft] = useState<string | null>(null);
  // Escape has to beat the blur it may trigger: otherwise the input losing
  // focus would commit the very draft the user just discarded.
  const discarded = useRef(false);

  async function commit(value: string) {
    if (discarded.current) return;
    setDraft(null);

    const name = value.trim();
    if (!name || name === trip.name) return;

    // Optimistic: the title is what the user is looking at, and flicking back
    // to the old one while the PATCH is in flight reads as a rejection.
    onRenamed({ ...trip, name });
    try {
      onRenamed(await tripsApi.update(trip.id, { name }));
    } catch {
      onRenamed(trip);
      toast(t('trip.renameFailed'), 'error');
    }
  }

  if (draft !== null) {
    return (
      <input
        type="text"
        autoFocus
        value={draft}
        maxLength={MAX_NAME}
        onChange={e => setDraft(e.target.value)}
        onBlur={e => commit(e.currentTarget.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') commit(e.currentTarget.value);
          if (e.key === 'Escape') { discarded.current = true; setDraft(null); }
        }}
        aria-label={t('trip.editName')}
        className={`${className} w-full min-w-0 bg-transparent border-b border-teal-400 focus:outline-none`}
      />
    );
  }

  if (!canEdit) return <p className={`${className} ${displayClassName}`}>{trip.name}</p>;

  return (
    <button
      type="button"
      onClick={() => { discarded.current = false; setDraft(trip.name); }}
      title={t('trip.editName')}
      className={`${className} ${displayClassName} w-full text-left cursor-text hover:underline decoration-dotted decoration-current underline-offset-4`}
    >
      {trip.name}
    </button>
  );
}
